import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Modal,
  Radio,
  Upload,
  Divider,
  Form,
  Input,
  Checkbox,
  InputNumber
} from 'antd';
import { TagOutlined, UploadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import useQuotesContext from './QuotesContext';

const { TextArea } = Input;

const alertStyle = {
  fontWeight: 700,
  fontSize: '0.875rem',
  fontStyle: 'italic',
  color: 'red'
};

const ExemptionModal = ({
  setFile,
  open,
  setVisible,
  openModal,
  exemptions,
  setModalExemption,
  isEmpty,
  hasClaims
}) => {
  const { setAnalyzeModalVisible } = useQuotesContext();
  const { t } = useTranslation();
  const [forcedExemption, setForcedExemption] = useState(false);
  const [fileList, setFileList] = useState(false);
  const { subPath: type } = useParams();

  const form = Form.useFormInstance();

  const unknownDesiredPremiumWatcher = Form.useWatch(
    'unknown_desired_cotisation',
    form
  );

  const handleModalSubmit = async () => {
    try {
      await form.validateFields();
      setAnalyzeModalVisible(true);
      setVisible(false);
    } catch {
      setAnalyzeModalVisible(false);
    }
  };

  const handleModalClose = () => {
    setForcedExemption(false);
    setModalExemption(false);
    setFile([]);
    setVisible(false);
  };

  const renderAlert = () => {
    if (isEmpty && ['mriml', 'mriwk', 'pnoml'].includes(type)) {
      return <p style={alertStyle}>{t('help.file_empty')}</p>;
    }
    if (hasClaims) {
      if (type === 'rcp')
        return <p style={alertStyle}>{t('help.claims_exemption_rc')}</p>;
      if (['mrp', 'mriml', 'pnoml', 'mriwk'].includes(type))
        return <p style={alertStyle}>{t('help.claims_exemption')}</p>;
    }
    return null;
  };

  return (
    <>
      {exemptions && exemptions.length === 0 && (
        <Button
          type="primary"
          className="exemption-button"
          onClick={() => {
            setForcedExemption(true);
            setModalExemption(true);
            openModal();
          }}
        >
          {`${t('buttons.voluntary_exemption')} `}
          <TagOutlined />
        </Button>
      )}

      <Modal
        title={
          forcedExemption
            ? t('buttons.voluntary_exemption')
            : t('buttons.exemption')
        }
        open={open}
        onOk={handleModalSubmit}
        onCancel={handleModalClose}
        width={800}
        closable={false}
      >
        <p style={{ fontWeight: 700 }}>
          {forcedExemption
            ? t('help.exemptions_request')
            : t('help.exemptions_request2')}
        </p>
        <Form.Item name="comment" rules={[{ required: true }]}>
          <TextArea
            placeholder={t('placeholder.request_object')}
            maxLength={3000}
            style={{ height: 150, marginBottom: 8 }}
          />
        </Form.Item>
        <Divider />
        <Form.Item
          name="desired_premium"
          label={t('help.exemption_prime')}
          rules={[{ required: !unknownDesiredPremiumWatcher }]}
          labelCol={{ span: 24 }}
        >
          <InputNumber
            min={1}
            defaultValue={1}
            addonAfter="€"
            style={{ width: 120 }}
            disabled={unknownDesiredPremiumWatcher}
          />
        </Form.Item>
        <Form.Item
          name="unknown_desired_cotisation"
          valuePropName="checked"
          help={
            unknownDesiredPremiumWatcher
              ? t('help.unknown_desired_cotisation_helper')
              : null
          }
        >
          <Checkbox>{t('help.unknown_desired_cotisation')}</Checkbox>
        </Form.Item>

        <Divider />
        {['mrp', 'rcp', 'association'].includes(type) && (
          <>
            <p style={{ fontWeight: 700 }}>{t('help.exemption_webSite')}</p>
            <Form.Item name="web_site">
              <Input />
            </Form.Item>
            <Divider />
          </>
        )}
        <p style={{ fontWeight: 700 }}>
          {t('buttons.upload_ask', { max: 5 })}
          {t('help.ask_for_files_exemption')}
        </p>
        {renderAlert()}
        <Upload
          multiple
          maxCount={5}
          fileList={fileList}
          onChange={({ fileList: newFileList }) => {
            setFileList(newFileList);
            const realFiles = newFileList
              .map((item) => item.originFileObj)
              .filter(Boolean);
            setFile(realFiles);
          }}
          beforeUpload={() => false}
        >
          <Button icon={<UploadOutlined />}>{t('buttons.upload')}</Button>
        </Upload>
        <Divider />
        <p style={{ fontWeight: 700 }}>{t('tasks.form.answer_delay')}</p>
        <Form.Item name="answerDelay">
          <Radio.Group style={{ flexFlow: 'row wrap' }} defaultValue="HIGH">
            <Radio value="URGENT">{t('exemptions_delay.very_urgent')}</Radio>
            <Radio value="HIGH">{t('exemptions_delay.urgent')}</Radio>
            <Radio value="NORMAL">{t('exemptions_delay.not_urgent')}</Radio>
            <Radio disabled value={t('exemptions_delay.more_than')}>
              {t('exemptions_delay.more_than')}
            </Radio>
          </Radio.Group>
        </Form.Item>
      </Modal>
    </>
  );
};

ExemptionModal.propTypes = {
  setFile: PropTypes.func.isRequired,
  setVisible: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  openModal: PropTypes.func.isRequired,
  exemptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  setModalExemption: PropTypes.func.isRequired,
  isEmpty: PropTypes.bool.isRequired,
  hasClaims: PropTypes.bool.isRequired
};

export default ExemptionModal;
